import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import { intlShape, injectIntl } from 'react-intl';
import TextField from '../../../../shared-components/TextField/TextField';
import { isEmpty } from '../../../../shared-components/FormElement/FormElementUtils';
import { VALID_PO_BOX_LIST } from './ValidPOBoxList';
import { US_MILITARY_STATES } from './../../../USConstants';
import PropTypes from 'prop-types';
import _ from 'lodash';

const LOCALE_PREFIX = 'US/pages/BillingAddressPage/BillingAddressForm';
// Pattern for POB and PMB preceded/followed by number or # or box (with or without spaces in between)
const RESTRICTED_POB_PMB_PATTERN = /((\d|#)\s*pob|pob\s*(\d|#)|(\d|#)\s*pmb|pmb\s*(\d|#|box))/i;
// Patterns to check for valid streets which can contain pob for example '4 poblano dr'.
const VALID_STREET_CONTAINING_POB_PATTERN = /((\d|#)\s*pob[a-zA-Z])/i;
// Patterns to check for atleast one letter.
const ATLEAST_ONE_ALPHABET_PATTERN = /[a-zA-Z]/;
// Pattern for Restricted special characters for ALP2
const RESTRICTED_ALP2_PATTERN = /\*|\t|\!|\?|\$|\%|\^|\&|\~|\`|\{|\}|\||\[|\]|\"|\<|\>|\@|\+|\=|\“|\”|\;|\:|\n/;

/**
 * We need to check for certain patterns for POB and PMB preceded/followed by
 * number or # (with or without space in between)
 */
export const checkForRestrictedPoBPattern = (value) => {
    return RESTRICTED_POB_PMB_PATTERN.test(value);
};

/**
 * We need to check for certain patterns for POB and PMB preceded/followed by
 * number or # (with or without space in between)
 */
export const checkForRestrictedALP2Pattern = (value) => {
    return RESTRICTED_ALP2_PATTERN.test(value);
};

// This function checks for valid streets which can contain pob like poblano.
export const checkForStreetContainingPOB = (value) => {
    return VALID_STREET_CONTAINING_POB_PATTERN.test(value);
};

export const logSplunkPOBoxAddress = (value, shouldLogPOBoxAddressInSplunk) => {
    if (shouldLogPOBoxAddressInSplunk) {
        capeLogSplunk && capeLogSplunk(`PO Box street address: clientSideValidation=yes poboxMatchFound=yes streetFieldValue=${value}`);
    }
};

export const logSplunkMilitaryAddress = (value, shouldLogMilitaryAddressInSplunk) => {
    if (shouldLogMilitaryAddressInSplunk) {
        capeLogSplunk && capeLogSplunk(`Military street address: clientSideValidation=yes militaryStateFound=yes streetFieldValue=${value}`);
    }
};

/**
 * If the value passed has atleast one alphabet(letter) then it is valid, else return error.
 * This function has been added to validate address line 1 as requested by SYF partner (to solve for account creation failures after approval)
 */
export const hasAtleastOneLetter = (msg, errorMsgClass) => (value) => {
    if (!ATLEAST_ONE_ALPHABET_PATTERN.test(value || '')) {
        return {
            msg,
            className: `hasError ${errorMsgClass}`
        };
    }
};

/**
 * Validate street line 1 and 2 for PO Box and PMB related strings when state in non-military.
 * PMB: Private Mail Box.
 */
export const isValidStreetLine = (msg, shouldLogPOBoxAddressInSplunk = false, shouldLogMilitaryAddressInSplunk = false) => (value, allValues) => {

    const upperCaseStreetLine = _.toUpper(value);

    // We do not want to check for pobox/pmb addresses when state value is not present.
    if (_.isEmpty(_.get(allValues, 'state'))) {
        return;
    }

    // We do not want to check for pobox/pmb addresses when its military state.
    if (_.get(allValues, 'state') in US_MILITARY_STATES) {
        // Although name is not correctly identifying, we want to tie all validation splunk logging for street here with same feature flag.
        logSplunkMilitaryAddress(value, shouldLogMilitaryAddressInSplunk);
        return;
    }

    // If address contains string denoting pobox addresses from given list,
    // we show field error notifying we don't accept such addresses.
    for (const listValue of VALID_PO_BOX_LIST) {
        if (_.includes(upperCaseStreetLine, listValue)) {
            logSplunkPOBoxAddress(value, shouldLogPOBoxAddressInSplunk);
            return {
                msg,
                className: 'hasError poBoxError'
            };
        }
    }

    // We need to check for certain patterns for POB and PMB preceded/followed by
    // number or # (with or without space in between) and also allow 'poblano'.
    if (checkForRestrictedPoBPattern(value) && !checkForStreetContainingPOB(value)){
        logSplunkPOBoxAddress(value, shouldLogPOBoxAddressInSplunk);
        return {
            msg,
            className: 'hasError poBoxError'
        };
    }

    return;
};

/**
 * Validate street line does not contain any restricted special characters required by SYF
 */
export const hasNoRestrictedInput = (msg) => (value) => {

    // Validate street line does not contain any restricted special characters
    if (checkForRestrictedALP2Pattern(value)){
        return {
            msg,
            className: 'hasError poBoxError'
        };
    }

    return;
};

export class StreetGroup extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        shouldLogPOBoxAddressInSplunk: PropTypes.bool,
        shouldLogMilitaryAddressInSplunk: PropTypes.bool
    };

    render() {
        const { intl, shouldLogPOBoxAddressInSplunk, shouldLogMilitaryAddressInSplunk } = this.props;

        return (
            <div>
                <Field
                    id="street1"
                    name="street1"
                    autoComplete="disabled"
                    autoCorrect="off"
                    autoCapitalize="off"
                    component={TextField}
                    label={intl.formatMessage({id: `${LOCALE_PREFIX}.address1-label`})}
                    POBox={{showToolTip:true,
                        POBoxMsg: intl.formatMessage({id: `${LOCALE_PREFIX}.poBox-tooltip`})
                    }}
                    validate={[
                        isEmpty(intl.formatMessage({id: `${LOCALE_PREFIX}.billinginfo-empty`})),
                        hasAtleastOneLetter(intl.formatMessage({id: `${LOCALE_PREFIX}.billinginfo-addressline1-invalid`}), 'poBoxError'),
                        isValidStreetLine(intl.formatMessage({id: `${LOCALE_PREFIX}.poBox-tooltip`}), shouldLogPOBoxAddressInSplunk, shouldLogMilitaryAddressInSplunk),
                        hasNoRestrictedInput(intl.formatMessage({id: `${LOCALE_PREFIX}.billinginfo-restricted-alp2-invalid`}))
                    ]}
                />
                <Field
                    id="street2"
                    name="street2"
                    autoComplete="disabled"
                    autoCorrect="off"
                    autoCapitalize="off"
                    component={TextField}
                    label={intl.formatMessage({id: `${LOCALE_PREFIX}.address2-label`})}
                    POBox={{showToolTip:false}}
                    validate={[
                        isValidStreetLine(intl.formatMessage({id: `${LOCALE_PREFIX}.poBox-tooltip`}), shouldLogPOBoxAddressInSplunk, shouldLogMilitaryAddressInSplunk),
                        hasNoRestrictedInput(intl.formatMessage({id: `${LOCALE_PREFIX}.billinginfo-restricted-alp2-invalid`}))
                    ]}
                    ref={this.setRefStreet2}
                    withRef={true}
                />
            </div>
        );
    }
}

export default connect()(injectIntl(StreetGroup));
